import React from "react";

/**
 * Route-level ErrorBoundary — catches uncaught render errors so a
 * single crashed component doesn't blank the entire tab (the
 * "white screen" iPad Safari showed on the check-in form when the
 * cmdk Command crashed on nationality select).
 *
 * On error we render a friendly retry card and log the payload to
 * the console so ops can pick it out of a bug report. `reset()`
 * clears the error state so a Reload button can restart the tree
 * without a full page reload — critical on the kiosk (tablet=1)
 * flow where `beforeunload` intentionally blocks navigation.
 */
export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Best-effort console dump so Safari's Web Inspector picks it up.
    // Intentionally not silent — the whole point is to make the crash
    // visible instead of blanking the page.
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary]", error, info?.componentStack || info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-[hsl(40_20%_97%)]"
        data-testid="route-error-boundary"
      >
        <div className="max-w-sm text-center">
          <div className="w-14 h-14 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center mx-auto text-2xl">
            !
          </div>
          <h1 className="font-editorial text-2xl mt-6">Something went wrong.</h1>
          <p className="text-sm text-muted-foreground mt-3">
            The page hit an unexpected error. Tap Reload to try again — your
            check-in link is still valid.
          </p>
          <div className="mt-6 flex flex-col gap-2 items-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full h-11 rounded-full bg-primary text-primary-foreground text-sm font-medium"
              data-testid="route-error-reload"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.reset}
              className="w-full h-11 rounded-full bg-white border border-border text-sm"
              data-testid="route-error-retry"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
